import React, { useMemo, useRef, type SyntheticEvent, useState } from 'react';
import { styled } from '@compiled/react';
import { parseISO } from 'date-fns';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { Text } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	main_softwareCodePage_RepositoriesList$data,
	main_softwareCodePage_RepositoriesList$key,
} from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_RepositoriesList.graphql';
import {
	COLLAPSIBLE_REGION_DESCRIPTION_ID,
	COLLAPSIBLE_REGION_ID,
	REPOSITORIES_HEADER_ID,
} from '../../../../../common/constants';
import { type SanitizedUrl, type Timestamp, numberToTimestamp } from '../../../../../common/types';
import EmptyStateCard from '../../../../../common/ui/empty-state-card';
import IsVisibleObserver from '../../../../../common/ui/is-visible-observer';
import { isBitbucketProvider, toSanitizedUrl } from '../../../../../common/utils';
import { useCodeLocalStore } from '../../../../../controllers';
import { useLocalStorage } from '../../../../../controllers/hooks';
import messages from './messages';
import { RepoCard as RepositoryCard } from './repository-card';

const LOCAL_STORAGE_DISMISSED_EMPTY_REPOS = 'dismissedEmptyRepoCards';
const useDismissedCards = () => useLocalStorage<string[]>(LOCAL_STORAGE_DISMISSED_EMPTY_REPOS, []);

type RepositoryInfo = {
	providerName?: string;
	providerId?: string;
	name: string;
	url: SanitizedUrl;
	type?: string;
	avatarUrl?: SanitizedUrl;
	description: string;
	lastUpdated: Timestamp;
	id: string;
};

const buildRepositoryInfo = ({
	providerName,
	providerType = undefined,
	providerId = undefined,
	id,
	avatarUrl,
	name,
	url,
	lastInferredAt,
}: {
	providerName: string;
	providerType?: string;
	providerId?: string;
	id: string;
	avatarUrl: string;
	name: string;
	url: string;
	lastInferredAt: string;
}): RepositoryInfo => ({
	providerName,
	providerId,
	name,
	url: toSanitizedUrl(url),
	type: providerType,
	avatarUrl: avatarUrl ? toSanitizedUrl(avatarUrl) : undefined,
	description: name,
	lastUpdated: numberToTimestamp(parseISO(lastInferredAt).valueOf()),
	id,
});

const lookupProviderName = (
	data: main_softwareCodePage_RepositoriesList$data,
	providerId: string,
): string | null => {
	const providers = data.devOps?.providers?.devInfoProviders?.filter(Boolean) ?? [];
	const maybeProvider = providers.find((provider) => provider?.id === providerId);
	return maybeProvider ? maybeProvider.name ?? null : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleLinkContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	margin: 0,
	marginBottom: token('space.100', '8px'),
	fontWeight: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardsContainer = styled.div<{ collapsed: boolean }>(
	{
		width: '100%',
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(225px, 1fr))',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		gridGap: `${gridSize}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ collapsed }) =>
		collapsed &&
		`
    max-height: 124px;
    margin: -5px;
    padding: 5px;
    overflow: hidden;
  `,
);

export const getRepositories = (
	data: main_softwareCodePage_RepositoriesList$data,
): (RepositoryInfo | null | undefined)[] =>
	data.jira.jiraProjectByKey.repositories
		? data.jira.jiraProjectByKey.repositories.edges.map((edge) => {
				const providerName = lookupProviderName(data, edge?.node?.providerId ?? '');
				if (providerName === null) {
					return null;
				}

				return buildRepositoryInfo({
					providerName,
					providerId: edge?.node?.providerId,
					name: edge?.node?.name ?? '',
					id: edge?.node?.id ?? '',
					avatarUrl: edge?.node?.avatarUrl ?? '',
					url: edge?.node?.url ?? '',
					lastInferredAt: edge?.lastUpdated ?? '',
				});
			})
		: [];

const useCollapsible = (
	expanded: boolean,
	id: string,
	labeledBy?: string,
	describedBy?: string,
) => ({
	buttonProps: {
		'aria-expanded': expanded,
		'aria-controls': id,
		role: 'button',
	},
	containerProps: {
		id,
		role: 'region',
		'aria-describedby': describedBy,
		'aria-labelledby': labeledBy,
	},
});

type RepositoriesProps = {
	repositoriesListData: main_softwareCodePage_RepositoriesList$key;
	isAdmin: boolean;
};

export const RepositoriesList = ({ repositoriesListData, isAdmin }: RepositoriesProps) => {
	const { formatMessage } = useIntl();
	const [codeLocalState, localActions] = useCodeLocalStore();
	const { buttonProps, containerProps } = useCollapsible(
		codeLocalState.showingAllRepositories,
		COLLAPSIBLE_REGION_ID,
		REPOSITORIES_HEADER_ID,
		COLLAPSIBLE_REGION_DESCRIPTION_ID,
	);
	const [hiddenCards, setHiddenCards] = useState(0);
	const collapsibleContainer = useRef<Element>(null);
	const [dismissedCards, setDismissedCards] = useDismissedCards();

	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_RepositoriesList on Query {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @required(action: THROW) {
						repositories(sort: { lastModified: { direction: DESC, priority: 1 } })
							@required(action: THROW)
							@optIn(to: "GraphStoreProjectAssociatedRepo") {
							edges @required(action: THROW) {
								lastUpdated @required(action: THROW)
								node {
									... on DevOpsRepository {
										id @required(action: THROW)
										providerId @required(action: THROW)
										name @required(action: THROW)
										avatarUrl
										url @required(action: THROW)
									}
								}
							}
						}
					}
				}
				devOps @required(action: THROW) {
					providers(id: $siteAri, providerTypes: [DEV_INFO])
						@required(action: THROW)
						@optIn(to: "DevOpsSecurityInJira") {
						devInfoProviders {
							__typename
							id @required(action: THROW)
							name
							homeUrl
						}
					}
				}
			}
		`,
		repositoriesListData,
	);

	const dataDepotProviders = useMemo(
		() => data.devOps?.providers?.devInfoProviders?.filter(Boolean) ?? [],
		[data],
	);

	const repositories = useMemo(() => getRepositories(data), [data]);

	const updateHiddenItemsCount = (isVisible: boolean) => {
		setHiddenCards((prevState) => (isVisible ? prevState - 1 : prevState + 1));
	};

	const RepoCards = useMemo(
		() =>
			repositories
				.flatMap((repo) => (repo ? [repo] : []))
				.map(({ name, providerName, url, lastUpdated, avatarUrl, id, type, providerId }) => (
					<IsVisibleObserver
						key={id}
						onVisibilityChange={updateHiddenItemsCount}
						root={collapsibleContainer}
					>
						<RepositoryCard
							key={id}
							repoName={name}
							providerName={providerName}
							providerId={providerId}
							repoUrl={url}
							iconUrl={avatarUrl}
							lastUpdated={lastUpdated}
							type={type}
						/>
					</IsVisibleObserver>
				)),
		[repositories, collapsibleContainer],
	);

	const EmptyStateCards = useMemo(
		() =>
			dataDepotProviders
				.filter((provider) => !dismissedCards.includes(provider.id))
				.filter(
					(provider) =>
						repositories.find((repository) => repository?.providerName === provider.name) == null,
				)
				.map((provider) => (
					<IsVisibleObserver
						key={provider.id}
						onVisibilityChange={updateHiddenItemsCount}
						root={collapsibleContainer}
					>
						<EmptyStateCard
							key={provider.id}
							providerName={provider.name ?? ''}
							simplified={codeLocalState.projectContext.simplified}
							projectId={codeLocalState.projectContext.projectId}
							url={toSanitizedUrl(provider.homeUrl ?? '')}
							setDismissedCards={() => setDismissedCards([...dismissedCards, provider.id])}
							isBitbucketProvider={isBitbucketProvider(provider.id)}
							isJiraGlobalAdmin={isAdmin}
							hasOtherRepositories={RepoCards.length > 0}
						/>
					</IsVisibleObserver>
				)),
		[
			dataDepotProviders,
			dismissedCards,
			repositories,
			codeLocalState.projectContext.simplified,
			codeLocalState.projectContext.projectId,
			isAdmin,
			RepoCards.length,
			setDismissedCards,
		],
	);

	const finalCardsList = useMemo(
		() => [...EmptyStateCards, ...RepoCards],
		[EmptyStateCards, RepoCards],
	);

	const containerList = useMemo(() => {
		const handleClick = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
			fireUIAnalytics(analyticsEvent, 'showHideReposLink', {
				expanded: !codeLocalState.showingAllRepositories,
			});
			localActions.setShowingAllRepositories(!codeLocalState.showingAllRepositories);
		};

		const getVisibleCount = (totalCount: number) => {
			if (codeLocalState.showingAllRepositories) {
				return totalCount;
			}
			return totalCount - hiddenCards;
		};

		return (
			<>
				<Heading level="h100" as="div">
					<CollapsibleLinkContainer>
						<Text id={COLLAPSIBLE_REGION_DESCRIPTION_ID}>
							{formatMessage(messages.repositoryListShowingRepositoryCount, {
								visible: getVisibleCount(finalCardsList.length),
								total: finalCardsList.length,
							})}
						</Text>
						{getVisibleCount(finalCardsList.length) < finalCardsList.length ||
						(getVisibleCount(finalCardsList.length) <= finalCardsList.length &&
							codeLocalState.showingAllRepositories) ? (
							<Button
								appearance="link"
								spacing="none"
								onClick={handleClick}
								iconAfter={
									codeLocalState.showingAllRepositories ? (
										<ChevronDownIcon size="small" label="" />
									) : (
										<ChevronRightIcon size="small" label="" />
									)
								}
								{...buttonProps}
								testId="software-code-page.ui.pages.main-page.repositories.repository-list.button"
							>
								{codeLocalState.showingAllRepositories
									? formatMessage(messages.repositoryListShowFewerLink)
									: formatMessage(messages.repositoryListShowAllLink)}
							</Button>
						) : null}
					</CollapsibleLinkContainer>
				</Heading>
				<CardsContainer
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					ref={collapsibleContainer as any}
					collapsed={!codeLocalState.showingAllRepositories}
					{...containerProps}
				>
					{finalCardsList}
				</CardsContainer>
			</>
		);
	}, [
		buttonProps,
		codeLocalState.showingAllRepositories,
		containerProps,
		finalCardsList,
		formatMessage,
		hiddenCards,
		localActions,
	]);

	if (RepoCards.length === 0) {
		// Adds an Analytics wrapper in case there is no repositories to display
		// TODO: re rendering this component seems to cause the CAD to re render all
		// children even if they havent changed.
		return (
			<ContextualAnalyticsData
				sourceName="codeConnectedButNoRepositoriesScreen"
				sourceType={SCREEN}
				attributes={{
					nextgen: codeLocalState.projectContext.simplified,
					projectId: codeLocalState.projectContext.projectId,
				}}
			>
				<FireScreenAnalytics />
				{containerList}
			</ContextualAnalyticsData>
		);
	}

	return containerList;
};
